import { Dispatch, Key, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Column as RdgColumn } from "react-data-grid";

import { EditFileBlueIcon, EllipseGroupBlueIcon, TransactionsBlueIcon, TrashDark } from "../../../../../assets";
import {
  ColorConstants,
  InteractionObjectType,
  InteractionType,
  LocalStorageConstants,
  OrganisationTypeConstants,
  ProjectIssuanceConstants,
  ProjectPermissionConstants,
  UserType,
} from "../../../../../constants";
import {
  CursorChangeProps,
  DataGridColumnCurrency,
  ProjectIssuanceBNG,
  ProjectIssuanceBNGPanelData,
  ProjectIssuanceCarbon,
  ProjectIssuanceCarbonPanelData,
  ResultType,
  SelectData,
} from "../../../../../models";
import {
  getProjectIssuances,
  GetProjectIssuancesResponse,
  getStandardSpecificSchemaDetails,
  searchInteractions,
} from "../../../../../service/query";
import { ResultData, Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject, jsonReviver, recursivelyValidateAndTransformDateToString } from "../../../../../utils";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
  StackedChartData,
} from "../../../../../widget";
import { ActionsIconCellFormatterData, EditColumns } from "../../../../../widget/data/DataGrid/models";
import { ProjectContext } from "../ProjectContext";

export const INVENTORY_STATUS_SELECT_DATA: SelectData = [
  { key: ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED, value: ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED },
  { key: ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT, value: ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT },
  { key: ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE, value: ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE },
];

interface UseInventoryReturnData {
  selectedGetProjectIssuancesResponseResults: GetProjectIssuancesResponse["results"] | undefined;
  onChange: DataGridCursorDataLoadEventHandler;
  isLoading: boolean;
  hasData: boolean;
  hasEnoughIssuancesForScrolling: boolean;
  columns: DataGridColumnDefinition[];
  unitMeasurement: string;
  stackedChartData: StackedChartData[] | undefined;
  exportFileName: string;
  xAxisLabels: (string | string[])[];
  currentUserType: UserType;
  defaultEditColumns: EditColumns;
  inventoryEditColumnsLocalStorageItemName: string;
  actionsMenuOptionsList: ProfileMenuItemType[];
  exportTable: boolean | undefined;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];

  showActionsMenu: boolean;
  setShowActionsMenu: Dispatch<SetStateAction<boolean>>;

  showEditColumnsPanel: boolean;
  setShowEditColumnsPanel: Dispatch<SetStateAction<boolean>>;

  showIssuancePanel: boolean;
  setShowIssuancePanel: (show: boolean) => void;

  hasIssuanceSchema: boolean;

  issuanceUuid: string | undefined;
  issuance: ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData | undefined;
  getProjectIssuanceResult: GetProjectIssuancesResponse["results"][0] | undefined;
  handleCloseIssuancePanel: () => void;

  showUnitTransactionsPanel: boolean;
  handleCloseUnitTransactionsPanel: () => void;

  showMultiIssuanceTransactionPanel: boolean;
  setShowMultiIssuanceTransactionPanel: Dispatch<SetStateAction<boolean>>;

  deleteIssuanceRowVersion: number | undefined;
  showDeleteIssuanceModal: boolean;
  handleCloseDeleteIssuanceModal: () => void;

  handleCloseMultiIssuanceTransactionPanel: () => void;

  refresh: boolean;
  handleRefresh: () => void;
  selectedRows: ReadonlySet<Key>;
  setSelectedRows: Dispatch<SetStateAction<ReadonlySet<Key>>> | undefined;

  tagsFilterSelectData: SelectData;
  selectedTagKeys: string[];
  onTagsFilter: (keys: string[]) => Promise<void>;
  selectedStatuses: string[];
  handleStatusFilter: (values: string[]) => void;
}

const getColumns = (unitMeasurement: string): DataGridColumnDefinition[] => {
  if (unitMeasurement === "Biodiversity") {
    return [
      {
        name: "Habitat group",
        key: "habitatType",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: true,
        sortable: true,
      },
      {
        name: "Habitat",
        key: "habitat",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: true,
        sortable: true,
      },
      {
        name: "Distinctiveness",
        key: "distinctiveness",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: true,
        sortable: true,
      },
      {
        name: "Serial Number",
        key: "serialNumber",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: true,
        sortable: true,
      },
      {
        name: "Issuance date",
        key: "issuanceDate",
        dataType: "Date",
        formatter: "dateOnly",
        minWidth: 150,
        filterable: false,
        sortable: true,
      },
      {
        name: "Available",
        key: "unitQuantityAvailable",
        dataType: "number",
        filterable: true,
        sortable: true,
        minWidth: 120,
      },
      {
        name: "Sold",
        key: "unitQuantitySold",
        dataType: "number",
        filterable: true,
        sortable: true,
        minWidth: 120,
      },
      {
        name: "% Sold",
        key: "percentageSold",
        formatter: "percentage",
        dataType: "number",
        filterable: false,
        sortable: true,
        minWidth: 120,
      },
      {
        name: "Reserved",
        key: "unitQuantityReserved",
        dataType: "number",
        filterable: true,
        sortable: true,
        minWidth: 120,
      },
      {
        name: "Price",
        key: "price",
        dataType: "number",
        formatter: "currency",
        currency: DataGridColumnCurrency.Gbp,
        minWidth: 100,
        filterable: true,
        sortable: true,
      },
      {
        name: "Unit type",
        key: "quantityType",
        dataType: "string",
        filterable: true,
        sortable: true,
        minWidth: 120,
      },
      {
        name: "Verifier",
        key: "verifier.displayName",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: true,
        sortable: true,
      },
      {
        name: "Actions",
        key: "inventoryActions",
        dataType: "string",
        formatter: "actionsIcon",
        alignment: "center",
        filterable: false,
        sortable: false,
        minWidth: 70,
      },
      {
        name: "Status",
        key: "status",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 120,
        filterable: true,
        sortable: true,
      },
      {
        name: "Unit size (Ha)",
        key: "unitHa",
        dataType: "number",
        formatter: "number",
        minWidth: 120,
        filterable: true,
        sortable: true,
      },
      {
        name: "Unit size (Km)",
        key: "unitKm",
        dataType: "number",
        formatter: "number",
        minWidth: 120,
        filterable: true,
        sortable: true,
      },
      {
        name: "Last updated",
        key: "updatedAt",
        dataType: "Date",
        formatter: "dateOnly",
        minWidth: 140,
        filterable: false,
        sortable: true,
      },
      {
        name: "Issuance ID",
        key: "issuanceUuid",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 320,
        filterable: false,
        sortable: false,
      },
      {
        name: "Created or enhanced",
        key: "createdOrEnhanced",
        dataType: "string",
        formatter: "stringWithEllipsis",
        filterable: false,
        sortable: false,
      },
    ];
  }
  return [
    {
      name: "Vintage",
      key: "vintage",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: false,
      sortable: true,
      minWidth: 120,
    },
    {
      name: "Serial Number",
      key: "serialNumber",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
      minWidth: 200,
    },
    {
      name: "Available",
      key: "unitQuantityAvailable",
      dataType: "number",
      filterable: true,
      sortable: true,
      minWidth: 120,
    },
    {
      name: "Sold",
      key: "unitQuantitySold",
      dataType: "number",
      filterable: true,
      sortable: true,
      minWidth: 120,
    },
    {
      name: "% Sold",
      key: "percentageSold",
      formatter: "percentage",
      dataType: "number",
      filterable: false,
      sortable: true,
      minWidth: 120,
    },
    {
      name: "Reserved",
      key: "unitQuantityReserved",
      dataType: "number",
      filterable: true,
      sortable: true,
      minWidth: 120,
    },
    {
      name: "Unit type",
      key: "quantityType",
      dataType: "string",
      filterable: true,
      sortable: true,
      minWidth: 120,
    },
    {
      name: "Price",
      key: "price",
      dataType: "number",
      formatter: "currency",
      currency: DataGridColumnCurrency.Gbp,
      minWidth: 100,
      filterable: true,
      sortable: true,
    },
    {
      name: "Verification status",
      key: "verificationStatus",
      dataType: "string",
      formatter: "stringWithEllipsis",
      minWidth: 180,
      filterable: true,
      sortable: true,
    },
    {
      name: "Verifier",
      key: "verifier.displayName",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: true,
    },
    {
      name: "Actions",
      key: "inventoryActions",
      dataType: "string",
      formatter: "actionsIcon",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    },
    {
      name: "Issuance date",
      key: "issuanceDate",
      dataType: "Date",
      formatter: "dateOnly",
      minWidth: 150,
      filterable: false,
      sortable: true,
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      minWidth: 120,
      filterable: true,
      sortable: true,
    },
    {
      name: "Last updated",
      key: "updatedAt",
      dataType: "Date",
      formatter: "dateOnly",
      minWidth: 140,
      filterable: false,
      sortable: true,
    },
    {
      name: "Issuance ID",
      key: "issuanceUuid",
      dataType: "string",
      formatter: "stringWithEllipsis",
      minWidth: 320,
      filterable: false,
      sortable: false,
    },
  ];
};

const getEditColumns = (unitMeasurement: string, userType: UserType): EditColumns => {
  const editColumns = getColumns(unitMeasurement).reduce((acc: EditColumns, col, index) => {
    let show = true;
    if (
      col.key === "status" ||
      col.key === "unitHa" ||
      col.key === "unitKm" ||
      col.key === "createdOrEnhanced" ||
      col.key === "updatedAt" ||
      col.key === "issuanceUuid" ||
      (col.key === "inventoryActions" &&
        userType !== OrganisationTypeConstants.DEVELOPER &&
        userType !== OrganisationTypeConstants.DEVELOPMENT_MANAGER) ||
      (col.key === "issuanceDate" && unitMeasurement !== "Biodiversity")
    )
      show = false;
    acc[col.key] = { show, index };
    return acc;
  }, {});

  return editColumns;
};

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  // Clone rows and Covert from date to string in dd/mm/yyyy format
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.inventoryActions !== undefined) delete row.inventoryActions;
  });
  return rowsClone;
};
const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "inventoryActions");
};

const getHabitatFromStandardSpecificIssuanceData = (jsonData: string): string[] => {
  const vintageData = JSON.parse(jsonData, jsonReviver) as ProjectIssuanceBNG;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return [vintageData.habitatType!, vintageData.habitatDistinctiveness!];
};

export const useInventory = (): UseInventoryReturnData => {
  const { currentUserType, user, currentOrganisationUuid } = useAuth();
  const { projectDetails, shouldRefreshProjectDetails, hasProjectPermission } = useContext(ProjectContext);
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughIssuancesForScrolling, setHasEnoughIssuancesForScrolling] = useState(false);

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>();
  const [xAxisLabels, setXAxisLabels] = useState<(string | string[])[]>([]);

  const [showIssuancePanel, setShowIssuancePanel] = useState(false);
  const [showDeleteIssuanceModal, setShowDeleteIssuanceModal] = useState(false);
  const [showEditColumnsPanel, setShowEditColumnsPanel] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showUnitTransactionsPanel, setShowUnitTransactionsPanel] = useState(false);
  const [showMultiIssuanceTransactionPanel, setShowMultiIssuanceTransactionPanel] = useState(false);

  const [getProjectIssuanceResult, setGetProjectIssuanceResult] = useState<GetProjectIssuancesResponse["results"][0]>();
  const [issuance, setIssuance] = useState<ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData>();
  const [issuanceUuid, setIssuanceUuid] = useState<string>();
  const [deleteIssuanceRowVersion, setDeleteIssuanceRowVersion] = useState<number | undefined>(undefined);

  const [getProjectIssuancesResponseResults, setGetProjectIssuancesResponseResults] =
    useState<GetProjectIssuancesResponse["results"]>();
  const [selectedGetProjectIssuancesResponseResults, setSelectedGetProjectIssuancesResponseResults] =
    useState<GetProjectIssuancesResponse["results"]>();

  const [refresh, setRefresh] = useState(false);
  const [exportTable, setExportTable] = useState<boolean>();

  const [hasIssuanceSchema, setHasIssuanceSchema] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<ReadonlySet<Key>>(new Set());
  const [tagsFilter, setTagsFilter] = useState<string[]>();
  const [tagsFilterSelectData, setTagsFilterSelectData] = useState<SelectData>([]);
  const [selectedTagKeys, setSelectedTagKeys] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED]);

  const isEditable = hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES);

  const unitMeasurement = useMemo(
    () => projectDetails?.unitMeasurements?.[0] ?? "Carbon",
    [projectDetails?.unitMeasurements]
  );

  const actionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit columns",
      action: () => setShowEditColumnsPanel(true),
    },
    {
      id: 2,
      value: "Export table",
      action: () => setExportTable(!exportTable),
    },
  ];

  const handleCloseIssuancePanel = (): void => {
    setShowIssuancePanel(false);
    setIssuance(undefined);
  };

  const handleCloseDeleteIssuanceModal = (): void => {
    setIssuanceUuid(undefined);
    setShowDeleteIssuanceModal(false);
  };

  const handleCloseUnitTransactionsPanel = (): void => {
    setIssuanceUuid(undefined);
    setShowUnitTransactionsPanel(false);
  };

  const handleCloseMultiIssuanceTransactionPanel = (): void => {
    setSelectedRows(new Set());
    setShowMultiIssuanceTransactionPanel(false);
  };

  const handleRefresh = (): void => {
    setRefresh((prev) => !prev);
    shouldRefreshProjectDetails();
  };

  const formatData = useCallback(
    (responseData: GetProjectIssuancesResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.issuanceUuid = el.uuid;

        const standardSpecificIssuanceData = JSON.parse(
          el.standardSpecificIssuanceData,
          jsonReviver
        ) as ProjectIssuanceCarbon & ProjectIssuanceBNG;

        if (isEditable) {
          result.inventoryActions = {
            icon: <EllipseGroupBlueIcon />,
            actions: [
              {
                id: 1,
                value: "Edit",
                icon: <EditFileBlueIcon width={20} height={20} />,
                action: () => {
                  setShowIssuancePanel(true);
                  setIssuance({
                    uuid: el.uuid,
                    price: el.price,
                    serialNumber: el.serialNumber,
                    quantityType: el.quantityType,
                    soldQuantity: el.unitQuantitySold,
                    reservedQuantity: el.unitQuantityReserved,
                    availableQuantity: el.unitQuantityAvailable,
                    status: el.status,
                    issuanceDate: el.issuanceDate,
                    standardSpecificIssuanceData,
                    rowVersion: el.rowVersion,
                    verifierUuid: el.verifier == null ? null : el.verifier.uuid,
                  });
                  setDeleteIssuanceRowVersion(el.rowVersion);
                },
              },
              {
                id: 2,
                value: "Delete",
                icon: <TrashDark width={20} height={20} className="InventoryDeleteIcon" />,
                action: () => {
                  setShowDeleteIssuanceModal(true);
                  setIssuanceUuid(el.uuid);
                  setDeleteIssuanceRowVersion(el.rowVersion);
                },
              },
              {
                id: 3,
                value: "Transactions",
                icon: <TransactionsBlueIcon />,
                action: () => {
                  setShowUnitTransactionsPanel(true);
                  setGetProjectIssuanceResult(el);
                },
              },
            ],
          } as ActionsIconCellFormatterData;
        }

        switch (unitMeasurement) {
          case "Carbon":
          case "Ecosystem recovery":
            result.vintage = `${el.vintageStartDate?.getFullYear()} - ${el.vintageEndDate?.getFullYear()}`;
            break;
          default:
            break;
        }
        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const sortKeyMap: Record<string, string> = {
      vintage: "vintageStartDate",
      issuanceUuid: "uuid",
    };

    if (tagsFilter && tagsFilter.length > 0) {
      filterCriteria.uuid = {
        operator: "in",
        value: tagsFilter,
      };
    }

    if (selectedStatuses.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (filterCriteria as any).status = {
        operator: "in",
        value: selectedStatuses,
      };
    }

    if (projectDetails)
      await getProjectIssuances({
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort:
          sorting.length > 0
            ? sorting.map((sort: { key: any; direction: any }) => ({
                key: sortKeyMap[sort.key] || sort.key,
                direction: sort.direction,
              }))
            : [
                {
                  key: unitMeasurement === "Biodiversity" ? "issuanceDate" : "vintageStartDate",
                  direction: "ASC",
                },
              ],
        /* eslint-enable */
        filter: { results: filterCriteria },
        request: { projectUuid: projectDetails.uuid, groupUuid: null },
      })
        .then((response) => {
          data = {
            resultData: formatData(response.data),
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: paging.pageSize,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            },
          };

          const keys: (string | string[])[] = [];
          const verifiedSold: number[] = [];
          const verifiedReserved: number[] = [];
          const verifiedAvailable: number[] = [];
          const pendingSold: number[] = [];
          const pendingReserved: number[] = [];
          const pendingAvailable: number[] = [];

          const results = response.data?.results ?? [];

          switch (unitMeasurement) {
            case "Biodiversity": {
              const groupedResults: {
                [key: string]: {
                  unitQuantitySold: number;
                  unitQuantityReserved: number;
                  unitQuantityAvailable: number;
                };
              } = {};

              results.forEach((value) => {
                const habitatType = getHabitatFromStandardSpecificIssuanceData(value.standardSpecificIssuanceData).join(
                  "-"
                );

                if (!groupedResults[habitatType]) {
                  groupedResults[habitatType] = {
                    unitQuantitySold: 0,
                    unitQuantityReserved: 0,
                    unitQuantityAvailable: 0,
                  };
                }

                groupedResults[habitatType].unitQuantitySold += value.unitQuantitySold;
                groupedResults[habitatType].unitQuantityReserved += value.unitQuantityReserved;
                groupedResults[habitatType].unitQuantityAvailable += value.unitQuantityAvailable;
              });

              Object.keys(groupedResults).forEach((key: string) => {
                keys.push(key.split("-"));
                verifiedSold.push(groupedResults[key].unitQuantitySold);
                verifiedReserved.push(groupedResults[key].unitQuantityReserved);
                verifiedAvailable.push(groupedResults[key].unitQuantityAvailable);
              });
              break;
            }
            case "Carbon":
            default: {
              results.forEach((value) => {
                keys.push(`${value.vintageStartDate?.getFullYear()}-${value.vintageEndDate?.getFullYear()}`);
                const isVerified =
                  value.quantityType === "WCU" || value.quantityType === "PCU" || value.quantityType === "WCC";
                verifiedSold.push(isVerified ? value.unitQuantitySold : 0);
                verifiedReserved.push(isVerified ? value.unitQuantityReserved : 0);
                verifiedAvailable.push(isVerified ? value.unitQuantityAvailable : 0);
                pendingSold.push(isVerified ? 0 : value.unitQuantitySold);
                pendingReserved.push(isVerified ? 0 : value.unitQuantityReserved);
                pendingAvailable.push(isVerified ? 0 : value.unitQuantityAvailable);
              });
              break;
            }
          }
          setHasData(results.length > 0);
          setHasEnoughIssuancesForScrolling(results.length > 5);
          setXAxisLabels(keys);
          setStackedChartData([
            {
              label: "Sold",
              data: pendingSold,
              backgroundColor: ColorConstants.INDIGO_500,
              hoverBackgroundColor: ColorConstants.INDIGO_400,
            },
            {
              label: "Reserved",
              data: pendingReserved,
              backgroundColor: ColorConstants.INDIGO_300,
              hoverBackgroundColor: ColorConstants.INDIGO_200,
            },
            {
              label: "Avaliable",
              data: pendingAvailable,
              backgroundColor: ColorConstants.INDIGO_100,
              hoverBackgroundColor: ColorConstants.BLUE_100,
            },
            {
              label: "Sold",
              data: verifiedSold,
              backgroundColor: ColorConstants.GREEN_600,
              hoverBackgroundColor: ColorConstants.GREEN_500,
            },
            {
              label: "Reserved",
              data: verifiedReserved,
              backgroundColor: ColorConstants.GREEN_400,
              hoverBackgroundColor: ColorConstants.GREEN_300,
            },
            {
              label: "Avaliable",
              data: verifiedAvailable,
              backgroundColor: ColorConstants.GREEN_200,
              hoverBackgroundColor: ColorConstants.GREEN_100,
            },
          ]);
          setGetProjectIssuancesResponseResults(response.data?.results);
        })
        .finally(() => {
          setIsLoading(false);
        });

    return data;
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (projectDetails?.standardSpecificSchemaUuid) {
        await getStandardSpecificSchemaDetails({
          standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid,
        }).then((response) => {
          if (
            response.status === Status.Success &&
            response.data &&
            response.data.issuanceSchema &&
            Object.keys(response.data.issuanceSchema).length > 0
          ) {
            setHasIssuanceSchema(true);
          }
        });
      }
    };

    fetchData();
  }, [projectDetails?.standardSpecificSchemaUuid]);

  useEffect(() => {
    setSelectedGetProjectIssuancesResponseResults(
      getProjectIssuancesResponseResults?.filter((i) => selectedRows.has(i.uuid))
    );
  }, [getProjectIssuancesResponseResults, selectedRows]);

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    setSelectedTagKeys(keys);

    if (keys.length === 0) {
      setTagsFilter(undefined);
      setRefresh((prev) => !prev);
      return;
    }

    const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

    searchInteractionsFilterCriteria.uuid = {
      operator: "in",
      value: keys,
    };

    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: searchInteractionsFilterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilter(
          response.data.results
            .filter((x) => x.objectInteractions.length > 0)
            .map((x) => x.objectInteractions.map((y) => y.objectUuid))
            .flat()
        );
      } else {
        setTagsFilter(undefined);
      }
      setRefresh((prev) => !prev);
    });
  };

  const getOrganisationTags = useCallback(async (organisationUuid: string): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = {
      operator: "eq",
      value: InteractionType.TAG,
    };
    filterCriteria.objectType = {
      operator: "eq",
      value: InteractionObjectType.ORGANISATION,
    };
    filterCriteria.objectUuid = {
      operator: "eq",
      value: organisationUuid,
    };

    filterCriteria.objectInteractions = {
      some: {
        objectType: {
          operator: "eq",
          value: InteractionObjectType.ISSUANCE,
        },
      },
    };
    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: filterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilterSelectData(
          response.data.results.map((x) => ({
            key: x.uuid,
            value: JSON.parse(x.data, jsonReviver).name,
          })) as SelectData
        );
      }
    });
  }, []);

  useEffect(() => {
    if (currentOrganisationUuid) getOrganisationTags(currentOrganisationUuid);
  }, [currentOrganisationUuid, refresh, getOrganisationTags]);

  return {
    selectedGetProjectIssuancesResponseResults,
    onChange,
    isLoading,
    hasData,
    hasEnoughIssuancesForScrolling,
    columns: getColumns(unitMeasurement),
    unitMeasurement,
    xAxisLabels,
    exportFileName: `${projectDetails?.displayName ? `${projectDetails.displayName}_` : ""}Inventory_`.replaceAll(
      " ",
      "_"
    ),
    stackedChartData,
    currentUserType,
    showIssuancePanel,
    actionsMenuOptionsList,
    exportTable,
    setShowIssuancePanel,
    showActionsMenu,
    setShowActionsMenu,
    showEditColumnsPanel,
    setShowEditColumnsPanel,
    getProjectIssuanceResult,
    issuance,
    handleCloseIssuancePanel,
    showUnitTransactionsPanel,
    handleCloseUnitTransactionsPanel,
    issuanceUuid,
    deleteIssuanceRowVersion,
    showDeleteIssuanceModal,
    handleCloseDeleteIssuanceModal,
    handleCloseMultiIssuanceTransactionPanel,
    refresh,
    handleRefresh,
    defaultEditColumns: getEditColumns(unitMeasurement, currentUserType),
    inventoryEditColumnsLocalStorageItemName: `${user?.userUuid ? `${user?.userUuid}_` : ""}${projectDetails?.unitMeasurements ? `${projectDetails?.unitMeasurements}_` : ""}${LocalStorageConstants.PROJECT_INVENTORY_PREFERENCES}`,
    filterExportColumns,
    filterExportRows,
    hasIssuanceSchema,
    selectedRows,
    setSelectedRows: isEditable ? setSelectedRows : undefined,
    showMultiIssuanceTransactionPanel,
    setShowMultiIssuanceTransactionPanel,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    selectedStatuses,
    handleStatusFilter: (values: string[]) => {
      setSelectedStatuses(values);
      setRefresh((prev) => !prev);
    },
  };
};
